import {
  Box,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiLogOut, FiMenu } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../components/Login/Logo";
import { getNavGroupsForUser, getPageLabel, Pages, ROLES } from "../constant";
import { logout } from "../redux/authSlice";
import { emptyCustomerDetails } from "../redux/companyDetailsSlice";
import { emptyCustomers } from "../redux/customerSlice";
import { emptyDealers } from "../redux/dealerSlice";
import { emptyProducts } from "../redux/productSlice";
import { emptyItems } from "../redux/qrSettingsSlice";
import Layout from "./Layout";

const SIDEBAR_COLLAPSED_KEY = "bill-sidebar-collapsed";

function readCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(next) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export default function Sidebar() {
  const dispatch = useDispatch();
  const { auth } = useSelector((state) => state);
  const { user } = auth;
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [page, setPage] = useState(user?.pagesPermissions?.[0] || null);
  const [isCollapsed, setIsCollapsed] = useState(readCollapsed);
  const component = searchParams.get("component");

  const navGroups = useMemo(() => getNavGroupsForUser(user), [user]);

  useEffect(() => {
    if (component) setPage(component);
  }, [location.search, component]);

  const navigate = useNavigate();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const isMobile = useBreakpointValue({ base: true, md: false });

  const sidebarBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const itemColor = useColorModeValue("gray.700", "gray.200");
  const itemHoverBg = useColorModeValue("gray.100", "gray.700");
  const activeBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const canvasBg = useColorModeValue("gray.50", "gray.900");

  const toggleCollapsed = () => {
    setIsCollapsed((c) => {
      const next = !c;
      writeCollapsed(next);
      return next;
    });
  };

  const handleLogout = () => {
    dispatch(logout());
    dispatch(emptyDealers());
    dispatch(emptyCustomers());
    dispatch(emptyProducts());
    dispatch(emptyCustomerDetails());
    dispatch(emptyItems());
    navigate("/");
  };

  const NavRow = ({ item, collapsed, onNavigate, isActive }) => {
    const content = (
      <>
        {item.icon ? (
          <Icon
            as={item.icon}
            boxSize={5}
            flexShrink={0}
            color={isActive ? "brand.600" : mutedColor}
          />
        ) : null}
        {!collapsed ? (
          <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
            {item.label}
          </Text>
        ) : null}
      </>
    );

    const rowStyles = {
      alignItems: "center",
      gap: 3,
      mx: 2,
      px: 3,
      py: 2.5,
      mb: 0.5,
      borderRadius: "md",
      cursor: item.isDisabled ? "not-allowed" : "pointer",
      opacity: item.isDisabled ? 0.45 : 1,
      transition: "background-color 0.2s ease, color 0.2s ease",
      bg: isActive ? activeBg : "transparent",
      color: isActive ? "brand.700" : itemColor,
      borderLeftWidth: "3px",
      borderLeftColor: isActive ? "brand.500" : "transparent",
      _hover:
        item.isDisabled || isActive
          ? undefined
          : { bg: itemHoverBg },
      width: "100%",
      textAlign: "left",
    };

    const onActivate = () => {
      if (item.isDisabled) return;
      setPage(item.name);
      onNavigate?.();
      navigate({ search: `?component=${item.name}` });
    };

    const button = (
      <Flex
        as="button"
        type="button"
        aria-current={isActive ? "page" : undefined}
        onClick={onActivate}
        {...rowStyles}
      >
        {content}
      </Flex>
    );

    if (collapsed) {
      return (
        <Tooltip label={item.label} placement="right" openDelay={400} hasArrow>
          {button}
        </Tooltip>
      );
    }

    return button;
  };

  const SidebarNavSections = ({ collapsed = false, onNavigate }) => (
    <Flex direction="column" gap={6} px={1} pb={6} pt={2}>
      {navGroups.map((group) => (
        <Box key={group.id}>
          {!collapsed ? (
            <Text
              px={3}
              mb={2}
              fontSize="xs"
              fontWeight="semibold"
              color={mutedColor}
              textTransform="uppercase"
              letterSpacing="0.06em"
            >
              {group.label}
            </Text>
          ) : (
            <Divider my={2} borderColor={borderColor} opacity={0.6} />
          )}
          {group.items.map((item) => {
            const isActive = searchParams.get("component") === item.name;
            return (
              <NavRow
                key={item.name}
                item={item}
                collapsed={collapsed}
                onNavigate={onNavigate}
                isActive={isActive}
              />
            );
          })}
        </Box>
      ))}
    </Flex>
  );

  const sidebarWidth = isCollapsed ? "16" : "64";

  const pageTitle = getPageLabel(component || page);

  return (
    <Box as="section" minH="100vh" bg={canvasBg}>
      {!isMobile ? (
        <Box
          as="aside"
          pos="fixed"
          top="0"
          left="0"
          zIndex="sticky"
          h="100vh"
          className="scrollbar-custom"
          overflowY="auto"
          overflowX="hidden"
          bg={sidebarBg}
          borderRightWidth="1px"
          borderColor={borderColor}
          w={{ md: sidebarWidth }}
          transition="width 0.2s ease"
          display="flex"
          flexDirection="column"
        >
          <Flex
            align="center"
            justify={isCollapsed ? "center" : "flex-start"}
            px={3}
            py={4}
            borderBottomWidth="1px"
            borderColor={borderColor}
            minH="14"
          >
            <Box overflow="hidden">
              <Logo className={isCollapsed ? "!w-9" : "!w-28"} />
            </Box>
          </Flex>

          <Box flex="1">
            <SidebarNavSections collapsed={isCollapsed} />
          </Box>

          <Box px={2} py={3} borderTopWidth="1px" borderColor={borderColor}>
            <Tooltip
              label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement="right"
            >
              <IconButton
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                icon={isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
                size="sm"
                variant="ghost"
                w="full"
                onClick={toggleCollapsed}
              />
            </Tooltip>
          </Box>
        </Box>
      ) : null}

      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent bg={sidebarBg} maxW="85vw">
          <DrawerHeader
            borderBottomWidth="1px"
            borderColor={borderColor}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text fontSize="md" fontWeight="semibold">
              Menu
            </Text>
            <DrawerCloseButton position="relative" top={0} right={0} />
          </DrawerHeader>
          <DrawerBody p={0}>
            <Box px={2}>
              <Flex align="center" justify="center" py={4}>
                <Logo className="!w-32" />
              </Flex>
              <SidebarNavSections collapsed={false} onNavigate={onClose} />
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box
        ml={{ base: 0, md: sidebarWidth }}
        transition="margin 0.2s ease"
        minH="100vh"
      >
        <Flex
          as="header"
          align="center"
          justify="space-between"
          w="full"
          px={{ base: 3, md: 6 }}
          borderBottomWidth="1px"
          borderColor={borderColor}
          bg={sidebarBg}
          minH="14"
          gap={3}
          position="sticky"
          top={0}
          zIndex="banner"
          shadow="sm"
        >
          <Flex align="center" gap={3} minW={0} flex="1">
            <IconButton
              aria-label="Open menu"
              display={{ base: "inline-flex", md: "none" }}
              icon={<FiMenu />}
              variant="ghost"
              onClick={onOpen}
            />
            <Text
              as="h1"
              fontSize={{ base: "md", md: "lg" }}
              fontWeight="semibold"
              color="gray.800"
              noOfLines={1}
              letterSpacing="-0.02em"
            >
              {pageTitle}
            </Text>
          </Flex>

          <Menu placement="bottom-end">
            <MenuButton
              py={2}
              px={3}
              borderRadius="md"
              fontSize="sm"
              fontWeight="medium"
              color="gray.700"
              _hover={{ bg: "gray.100" }}
              transition="background 0.15s ease"
            >
              {user?.firstName
                ? `${user.firstName}${
                    user?.lastName ? ` ${user.lastName}` : ""
                  }`
                : "Account"}
            </MenuButton>
            <MenuList borderRadius="md" shadow="md" py={1}>
              <MenuItem
                icon={<FiLogOut />}
                onClick={handleLogout}
                fontSize="sm"
                borderRadius="md"
              >
                Log out
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>

        <Layout>
          {Pages.map((item) => {
            if (!item.permission.includes(user?.role)) return null;
            if (item.name === page) {
              return (
                <React.Fragment key={item.name}>
                  {React.cloneElement(item.component, { key: item.name })}
                </React.Fragment>
              );
            }
            return null;
          })}
        </Layout>
      </Box>
    </Box>
  );
}
